import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { EMPTY_URL } from "csi/constants";
import def from "csi/def";
import { View } from "csi/def/View";
import enums from "csi/enums";
import reflection from "csi/reflection";
import { SearchCallback, getSearch, runSearch } from "csi/service/dataStoreService";
import { resolveNodesWithDependencies } from "csi/service/nodeService";
import session from "csi/session";
import WorkingCache from "csi/state/WorkingCache";
import { routerStore, sessionStore } from "csi/state/rootStore";
import { C8Node, ICache, cnl } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import { LOCATION_TAB, LOCATION_URL } from "csi/utility/routerUtility";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { green, grey, orange, red } from "designSystem/styles/color";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ManageProofStore } from "./Proof/ManageProofStore";
import ProofingStore from "./Proof/ProofingStore";
import Memory from "dojo/store/Memory";
import locale from "csi/locale";
import Store from "dojo/store/api/Store";

export const STAGE_TRIGGER_TYPE_ENUM_NAME = "StageTriggerType";

//Due Date
export const DUE_DATE_NONE = -1;
export const DUE_DATE_SPECIFIC_DATE = -2;
export const DUE_DATE_STAGE_START_DAY = 0;

export const PROOF_DECISION_STATUS_ENUM_NAME = "ProofDecisionStatus";

export const PROOF_DECISION_STATUS = {
	APPROVED: `${PROOF_DECISION_STATUS_ENUM_NAME}:Approved`,
	APPROVED_WITH_CHANGES: `${PROOF_DECISION_STATUS_ENUM_NAME}:ApprovedWithChanges`,
	CHANGES_REQUIRED: `${PROOF_DECISION_STATUS_ENUM_NAME}:ChangesRequired`,
	DECISION_PENDING: `${PROOF_DECISION_STATUS_ENUM_NAME}:DecisionPending`,
	FAILED: `${PROOF_DECISION_STATUS_ENUM_NAME}:Failed`,
	IN_PROGRESS: `${PROOF_DECISION_STATUS_ENUM_NAME}:InProgress`,
	NEW_VERSION_AVAILABLE: `${PROOF_DECISION_STATUS_ENUM_NAME}:NewVersionAvailable`,
	NOT_STARTED: `${PROOF_DECISION_STATUS_ENUM_NAME}:NotStarted`,
	REVIEW_COMPLETE: `${PROOF_DECISION_STATUS_ENUM_NAME}:ReviewComplete`,
	UPLOADING: `${PROOF_DECISION_STATUS_ENUM_NAME}:Uploading`,
	VIEW_ONLY: `${PROOF_DECISION_STATUS_ENUM_NAME}:ViewOnly`,
};

export const DECISION_STATUS_ITEMS_FROM_APPROVER = [
	PROOF_DECISION_STATUS.APPROVED,
	PROOF_DECISION_STATUS.APPROVED_WITH_CHANGES,
	PROOF_DECISION_STATUS.CHANGES_REQUIRED,
	PROOF_DECISION_STATUS.DECISION_PENDING,
];

export const DECISION_STATUS_ITEMS_FOR_REVIEWER = [
	PROOF_DECISION_STATUS.IN_PROGRESS,
	PROOF_DECISION_STATUS.REVIEW_COMPLETE,
];

export enum STAGE_TRIGGER {
	IMMEDIATELY = "IMMEDIATELY",
	STARTSWHEN = "STARTSWHEN",
}

export const STAGE_TRIGGER_OPTIONS: ComponentItem[] = [
	{ id: "0", res: "ProofStageStartImmediately", value: STAGE_TRIGGER.IMMEDIATELY },
	{ disabled: true, id: "1", res: "ProofStageStartWhen", value: STAGE_TRIGGER.STARTSWHEN },
];

const STAGE_COMMENTS_ENUM_NAME = "StageCommentsType";

const STAGE_COMMENTS_STORE = enums.getStore(STAGE_COMMENTS_ENUM_NAME, null, null, true);
export const STAGE_COMMENTS_OPTIONS_QUERY = STAGE_COMMENTS_STORE.query();

const STAGE_TRIGGER_TYPE_STORE = enums.getStore(STAGE_TRIGGER_TYPE_ENUM_NAME, null, null, true);
export const STAGE_TRIGGER_TYPE_OPTIONS_QUERY = STAGE_TRIGGER_TYPE_STORE.query();

export const STAGE_COMMENTS_PUBLIC_ONLY = xml.enumCreate(STAGE_COMMENTS_ENUM_NAME, "PublicOnly");

export const GENERIC_UNNAMED_STAGE = "GenericUnnamedStage";

export const STAGE_TRIGGER_TYPE = {
	IMMEDIATELY: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:Immediately`,
	MANUAL: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:Manual`,
	DEADLINE_REACHED: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:DeadlineReached`,
	ON_COMPLETION: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:OnCompletion`,
	ON_APPROVED: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:OnApproved`,
	ON_APPROVED_WITH_CHANGES: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:OnApprovedWithChanges`,
	ON_CHANGES_REQUIRED: `${STAGE_TRIGGER_TYPE_ENUM_NAME}:OnChangesRequired`,
};

export const EXCLUDE_ITEMS = [STAGE_TRIGGER_TYPE.MANUAL, STAGE_TRIGGER_TYPE.DEADLINE_REACHED];

export const APPROVER = reflection.URL_INSTANCE + "ProofTaskDefinition/Approver";
export const REVIEWER = reflection.URL_INSTANCE + "ProofTaskDefinition/Reviewer";
export const VIEWER = reflection.URL_INSTANCE + "ProofTaskDefinition/Viewer";
export const USE_CUSTOM = "Custom";
export const USE_TEMPLATE = "UseTemplate";
export const FINAL_DECISION_APPROVER_RESOURCE = "FinalDecisionCalculationGroupApprover";

export const ROLE_PREFIX = "centric://REFLECTION/INSTANCE/Role";

export const DUPLICATE_USER_ERROR_RES = "ProofingDuplicateUser";
export const PERMISSION_REQUIRED_ERROR_RES = "ProofingPermissionRequired";
export const REQUIRED_USER_ERROR_RES = "ProofingUserRequiredError";
export const USER_WITH_SAME_EMAIL_ERROR_RES = "ProofingUserWithSameEmailError";

export const PROOF_PERMISSIONS: ComponentItem[] = [
	{ res: "ProofAccessViewer", id: VIEWER },
	{ res: "ProofAccessReviewer", id: REVIEWER },
	{ res: "ProofAccessApprover", id: APPROVER },
];

export function defProofProgressView(): View {
	const rc = reflection.getClass("ProofWorkflowInstance");
	const defView = def.getClassViewDef(rc, "ProofManagement") as View;
	return defView;
}

/** fetch proof workflow and dependencies down to user details */
export async function getProofDocumentWithDependencies(documentCnl: cnl, cache: ICache): Promise<C8Node> {
	const node = await resolveNodesWithDependencies(
		[documentCnl],
		["Child:Revisions/Child:ProofWorkflow/Child:Subprocesses/Child:Tasks/Child:Assignee/Child:Details"],
		cache,
	);
	return node[0];
}

/** fetch is approved with changes option availability */
export function fetchIsApprovedWithChangesDisabled(): Promise<JsonResponse> {
	const req = new PostRequest("Proofing", "IsApprovedWithChangesDisabled");
	return req.go();
}

/** check if the current stage template has "immediately" trigger */
export function isImmediately(template: C8Node): boolean {
	return template.TriggerType === STAGE_TRIGGER_TYPE.IMMEDIATELY;
}

/** options for due date */
export const DUE_DATE_OPTIONS: ComponentItem[] = [
	{ id: DUE_DATE_NONE.toString(), res: "None", value: DUE_DATE_NONE },
	{ id: DUE_DATE_SPECIFIC_DATE.toString(), res: "SpecificDate", value: DUE_DATE_SPECIFIC_DATE },
	{ id: DUE_DATE_STAGE_START_DAY.toString(), res: "DueDateStageStartDay", value: DUE_DATE_STAGE_START_DAY },
];

for (let i = 1; i <= 20; i++) {
	DUE_DATE_OPTIONS.push({ id: i.toString(), res: "DueDateThresholdDays", resParams: [i], value: i });
}

const LOCK_STAGE_ENUM_NAME = "LockStageType";
const LOCK_STAGE_STORE = enums.getStore(LOCK_STAGE_ENUM_NAME, null, null, true);
export const LOCK_STAGE_OPTIONS_QUERY = LOCK_STAGE_STORE.query();
export const LOCK_STAGE_NEVER = xml.enumCreate(LOCK_STAGE_ENUM_NAME, "Never");

const FINAL_STAGE_DECISION_CALCULATION_ENUM_NAME = "FinalStageDecisionCalculationType";
const FINAL_STAGE_DECISION_CALCULATION_STORE = enums.getStore(FINAL_STAGE_DECISION_CALCULATION_ENUM_NAME, null, null, true);
export const FINAL_STAGE_DECISION_CALCULATION_OPTION_QUERY = FINAL_STAGE_DECISION_CALCULATION_STORE.query();
export const FINAL_STAGE_DECISION_CALCULATION_ALL_DECISIONS = xml.enumCreate(FINAL_STAGE_DECISION_CALCULATION_ENUM_NAME, "AllDecisions");
export const FINAL_STAGE_DECISION_CALCULATION_DECISION_MADE_BY = xml.enumCreate(FINAL_STAGE_DECISION_CALCULATION_ENUM_NAME, "DecisionMadeBy");

/** generate the store for the due date from the template dojo table */
export function generateDojoStoreForDueDate(): Store<object, object, object> {
	const DUE_DATE_DATA = DUE_DATE_OPTIONS.map((option) => ({ id: Number(option.id), name: locale.resource(option.res, option.resParams) }));

	return new Memory({
		data: DUE_DATE_DATA,
		idProperty: "id",
		labelProperty: "name",
	});
}

/** calculate the DueDate based on the threshold */
export function calculateDueDate(threshold: number): Date | null {
	if (threshold < 0) {
		return null;
	}

	const now = new Date();

	// add x day(s)
	now.setDate(now.getDate() + threshold);

	const finalDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59));
	finalDate.setMinutes(finalDate.getMinutes() + finalDate.getTimezoneOffset());

	return finalDate;
}

/** Fetch users with proofing roles, specify admin only for Proof Administrator or Site Administrator only. */
export function fetchUsersWithProofingRoles(adminOnly?: boolean, cache?: ICache, crewUrl?: cnl, cb?: SearchCallback): Promise<JsonResponse> {
	const search = getSearch("User.Available");
	search.addParameter("RestrictToCrew", crewUrl || EMPTY_URL);
	const proofRoles = [
		xml.ROLE_ADMIN,
		`${ROLE_PREFIX}/Proof Administrator`,
	];
	if (!adminOnly) {
		proofRoles.push(`${ROLE_PREFIX}/Proof Editor`);
		proofRoles.push(`${ROLE_PREFIX}/Proof Viewer`);
		search.addDependencyPaths([pathCreate("Details")]);
	}
	search.addParameter("RestrictToParties", proofRoles);
	search.addParameter("IncludeGlobal", true);

	return runSearch(search, cache, cb);
}

const EMAIL_NOTIFICATION_OPTIONS_ENUM_NAME = "EmailNotification";

const EMAIL_NOTIFICATION_OPTIONS_STORE = enums.getStore(EMAIL_NOTIFICATION_OPTIONS_ENUM_NAME, null, null, true);
export const EMAIL_NOTIFICATION_OPTIONS_QUERY = EMAIL_NOTIFICATION_OPTIONS_STORE.query();
export const EMAIL_NOTIFICATION_DEFAULT_OPTION_NAME = xml.enumCreate(EMAIL_NOTIFICATION_OPTIONS_ENUM_NAME, "HourlyDigest");

export const ENUM_APPROVED_WITH_CHANGES = "ApprovedWithChanges";
export const ENUM_APPROVED = "Approved";
export const ENUM_FAILED = "Failed";
export const ENUM_REVIEW_COMPLETE = "ReviewComplete";
export const ENUM_UPLOADING = "Uploading";

export const TASK_STATUS_ICONS = {
	[ENUM_APPROVED_WITH_CHANGES]: { icon: "check_circle_outline", color: green[600] },
	[ENUM_APPROVED]: { icon: "check_circle_outline", color: green[600] },
	ChangesRequired: { icon: "refresh", color: red[600] },
	DecisionPending: { icon: "access_time", color: orange[600] },
	[ENUM_FAILED]: { icon: "cancel", color: red[500] },
	InProgress: { icon: "access_time", color: orange[600] },
	NotStarted: { icon: "access_time", color: grey[1000] },
	[ENUM_UPLOADING]: { icon: "", color: grey[1000] },
	[ENUM_REVIEW_COMPLETE]: { icon: "check_circle_outline", color: green[600] },
	ViewOnly: { icon: "remove_red_eye", color: grey[1000] },
	NewVersionAvailable: { icon: "error_outline", color: orange[600] },
	Locked: { icon: "lock", color: grey[1000] },
};

/** exit full screen mode, get out of Ziflow iFrame, if there is a tab, use it */
export function goBackToPreviousNode(): void {
	const { targetPreviousUrl, targetTab } = routerStore;

	if (targetTab) {
		session.setLocationActual(LOCATION_TAB, targetTab);
	}

	session.setLocationKey(LOCATION_URL, targetPreviousUrl || reflection.URL_HOME, true);
}

/** create proofing store */
export async function createProofingStore(document: C8Node, loc: ILocaleProvider, isEditOperation: boolean): Promise<ProofingStore> {
	const cache = new WorkingCache();
	await getProofDocumentWithDependencies(document.$URL, cache);
	const store = new ProofingStore(document, loc, isEditOperation, cache);
	await store.fetchActiveUsers();
	await store.fetchActiveUsers(true);
	return store;
}

/** create the manage proof store, return null for no access */
export async function createManageProofStore(): Promise<ManageProofStore> | null {
	const document = routerStore.getBreadcrumbNode("Document");

	// if user is not a document viewer, go back
	if (!document) {
		goBackToPreviousNode();
		return null;
	}

	const cache = new WorkingCache();
	await getProofDocumentWithDependencies(document.$URL, cache);
	const documentRevision = cache.get(document.CurrentRevision);
	const proofWorkflowInstanceCnl = documentRevision.ProofWorkflow;
	const documentRevisionCnl = documentRevision.$URL;
	const proofWorkflowInstance = cache.get(proofWorkflowInstanceCnl);

	const isLegacyProof = proofWorkflowInstance.LegacyProof;

	// if user is not in the proof as an assignee, go back
	if (!isUserInProof(proofWorkflowInstance)) {
		goBackToPreviousNode();
		return null;
	}

	const postRequest = new PostRequest("Proofing", "ProofUrl")
		.addParameter("ProofId", proofWorkflowInstance.Id);

	let proofUrl: cnl;
	let isUserProofAdmin: boolean;
	try {
		const url: JsonResponse = await postRequest.go();
		const commentId = new URLSearchParams(location.search)
			.get("comment_id");
		if (commentId) {
			proofUrl = (`${url.getReturnValue()}#${commentId}`);
		} else {
			proofUrl = (`${url.getReturnValue()}`);
		}
		isUserProofAdmin = (await fetchIsUserProofAdmin(sessionStore.userUrl, documentRevisionCnl)).getReturnValue();
	} catch (ex) {
		console.error(ex);
		goBackToPreviousNode();
		return null;
	}

	const proofDocumentName = routerStore.getNodeLocationName(document.$URL);

	return new ManageProofStore(
		cache,
		documentRevisionCnl,
		proofWorkflowInstanceCnl,
		proofDocumentName,
		proofUrl,
		isUserProofAdmin,
		isLegacyProof,
	);
}

/**
 * 
 * @param iconValue => determines the icon and icon color
 * @param iconOnly => determines if text is included
 * @param iconLeft => places icon to the left of text (right by default)
 * @param textValue => value that displays next to icon
 * @returns a set parameters for icon based on parameters given
 */
export const getIconParameters = (iconValue: string, iconOnly: boolean, iconLeft: boolean, textValue?: string): Object => {
	return {
		class: "csi-flex-inline",
		removeIconPointerCursor: true,
		icon: TASK_STATUS_ICONS[iconValue].icon,
		iconColor: TASK_STATUS_ICONS[iconValue].color,
		iconOnly,
		iconLeft,
		text: textValue,
	};
};

/** Returns whether the user is a proof administrator(Global level and Team level) */
export function fetchIsUserProofAdmin(userURL: cnl, documentRevisionURL?: cnl): Promise<JsonResponse> {
	const req = new PostRequest("Proofing", "IsUserProofAdmin");
	req.addParameter("URL", userURL);

	if (documentRevisionURL) {
		req.addParameter("DocumentRevision", documentRevisionURL);
	}

	return req.go();
}

/** Get whether user is in proof */
export function isUserInProof(proofWorkflow: C8Node): boolean {
	const userUrl = sessionStore.userUrl;
	return proofWorkflow.Participants.includes(userUrl);
}

/**
 * Send a request to lock or unlock the proof depending on the current lock state of the proof.
 * @param lock 
 * @param proofWorkflowInstance 
 * @param documentRevision 
 */
export function manageProofLock(lock: boolean, proofWorkflowInstance: cnl, documentRevision: cnl): void {
	const request = new PostRequest("Proofing", lock ? "LockProof" : "UnlockProof");
	request.addParameter("proofWorkflowInstance", proofWorkflowInstance)
		.addParameter("Document", documentRevision);
	request.post();
};

export function isLockedAndStarted(stageNode: C8Node): boolean {
	return stageNode.StageIsLocked && stageNode.DecisionStatus !== "ProofDecisionStatus:NotStarted";
}